import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  downloadCrusherReportPdf,
  downloadIotDeviceReportPdf,
} from "./pdfgen.jsx";
import {
  ComposedChart,
  Area,
  XAxis,
  Tooltip,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const UserDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // const [deviceData, setDeviceData] = useState({});
  const [deviceDataMap, setDeviceDataMap] = useState({});
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [showValueBoxes, setShowValueBoxes] = useState(false);
  const [chartData, setChartData] = useState([]);

  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [rangeType, setRangeType] = useState("Daily");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("User_Id");

  const [crusherSummary, setCrusherSummary] = useState(null);
  const [crusherTodayTotals, setCrusherTodayTotals] = useState(null);
  const [chartHScrollEnabled, setChartHScrollEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  const chartScrollWidthPx = useMemo(() => {
    if (!chartData?.length) return null;
    return Math.min(Math.max(chartData.length * 12 + 100, 320), 2800);
  }, [chartData]);

  const chartRef = useRef(null);

  const formatChartAxisTick = useMemo(() => {
    return (timestamp) => {
      const d = new Date(timestamp);
      if (!Number.isFinite(d.getTime())) return "";
      if (rangeType === "Monthly") {
        return d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });
      }
      if (rangeType === "Weekly") {
        return d.toLocaleDateString("en-IN", {
          weekday: "short",
          day: "2-digit",
        });
      }
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };
  }, [rangeType]);

  const [, setChartScrollTick] = useState(0);

  useEffect(() => {
    const el = chartRef.current;
    if (!el || !showGraph) return;
    const onScroll = () => setChartScrollTick((t) => t + 1);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [showGraph, chartData.length]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setChartHScrollEnabled(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isCrusherDeviceCheck = (device) => {
    if (!device) return false;

    const id = String(device.deviceId || "").toLowerCase();
    const name = String(device.name || "").toLowerCase();

    return id.startsWith("20") && name.includes("crusher");
  };

  // ------------------------- Fetch Projects -------------------------
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await axios.get(
          `https://api.livestockify.com/api/chennai/projects/client/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setProjects(res.data);
      } catch (err) {
        console.error("Fetching project data:", err);
      }
    };
    fetchProjects();
  }, [token, userId]);

  // ------------------------- Auto Fetch All Device Data on Load -------------------------
  useEffect(() => {
    if (!projects?.devices) return;

    projects.devices.forEach(async (device) => {
      try {
        // Crusher Device
        if (isCrusherDeviceCheck(device)) {
          const res = await axios.get(
            `https://api.livestockify.com/api/crusher-devices/totals?deviceId=${device.deviceId}&type=today`,
          );

          setCrusherTodayTotals(res.data);
          return;
        }

        // Normal Device
        else {
          const res = await axios.get(
            `https://api.livestockify.com/api/chennai/iot/data/${device.deviceId}/1`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const sensors = res.data?.[0]?.sensors || {};

          setDeviceDataMap((prev) => ({
            ...prev,
            [device.deviceId]: sensors,
          }));
        }
      } catch (err) {
        console.error("Auto device fetch error:", err);
      }
    });
  }, [projects]);

  // ------------------------- Auto select first device -------------------------
  useEffect(() => {
    if (selectedDevice || !projects?.devices?.length) return;

    const firstDevice = projects.devices[0];
    setSelectedDevice(firstDevice);
    setShowValueBoxes(true);
    setShowGraph(false);

    if (isCrusherDeviceCheck(firstDevice)) {
      fetchCrusherTodayTotals(firstDevice.deviceId);
    }
  }, [projects, selectedDevice]);

  // ------------------------- Fetch Device Data -------------------------
  useEffect(() => {
    const fetchDeviceData = async () => {
      if (!selectedDevice) return;

      if (isCrusherDeviceCheck(selectedDevice)) return;
      try {
        const res = await axios.get(
          `https://api.livestockify.com/api/chennai/iot/data/${selectedDevice.deviceId}/1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          const sensors = res.data[0].sensors || {};
          // setDeviceData(sensors);
          setDeviceDataMap((prev) => ({
            ...prev,
            [selectedDevice.deviceId]: sensors,
          }));
        } else {
          // setDeviceDataMap({});
          setDeviceDataMap((prev) => ({
            ...prev,
            [selectedDevice.deviceId]: {},
          }));
        }
      } catch (err) {
        console.error("Error fetching IoT data:", err);
        // setDeviceDataMap({});
        setDeviceDataMap((prev) => ({
          ...prev,
          [selectedDevice.deviceId]: {},
        }));
      }
    };
    fetchDeviceData();
  }, [selectedDevice]);

  // ------------------------- Auto-select metric -------------------------

  useEffect(() => {
    if (
      selectedDevice &&
      deviceDataMap[selectedDevice?.deviceId] &&
      Object.keys(deviceDataMap[selectedDevice?.deviceId]).length > 0
    ) {
      const firstMetric = Object.keys(
        deviceDataMap[selectedDevice.deviceId],
      ).find((key) => key.toLowerCase() !== "timestamp");

      setSelectedMetric(firstMetric);
      setShowGraph(false); // ❗ stop auto graph
      setChartData([]);
    }
  }, [deviceDataMap, selectedDevice]);

  // ------------------------- Handle Device Click -------------------------

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowValueBoxes(true);
    setShowGraph(false);

    // if (String(device.deviceId) === "2024")
    if (isCrusherDeviceCheck(device)) {
      fetchCrusherTodayTotals(device.deviceId);
    }
  };

  // ------------------------- Handle Metric Click -------------------------
  const handleMetricClick = (metricName) => {
    setSelectedMetric(metricName);
    setShowGraph(true);
    // setChartData([]);
    setRangeType((prev) => prev);
  };

  // ------------------------- Fetch Graph Data -------------------------

  useEffect(() => {
    if (!selectedDevice) return;

    // 🟢 If Crusher → call crusher API
    if (isCrusherDevice) {
      if (rangeType === "Custom") {
        if (startDate && endDate)
          fetchCrusherSummary("custom", startDate, endDate);
      } else if (rangeType === "Daily") {
        fetchCrusherSummary("today");
      } else if (rangeType === "Weekly") {
        fetchCrusherSummary("weekly");
      } else if (rangeType === "Monthly") {
        fetchCrusherSummary("monthly");
      }

      return; // 🚨 stop normal API
    }

    // 🔵 Normal devices (existing code)
    if (!selectedMetric) return;

    const fetchGraphByRange = async (
      range,
      customStart = null,
      customEnd = null,
    ) => {
      let url = "";

      if (range === "Daily") {
        url = `https://api.livestockify.com/api/chennai/iot/daily?deviceId=${selectedDevice.deviceId}`;
      } else if (range === "Weekly") {
        url = `https://api.livestockify.com/api/chennai/iot/week?deviceId=${selectedDevice.deviceId}`;
      } else if (range === "Monthly") {
        url = `https://api.livestockify.com/api/chennai/iot/month?deviceId=${selectedDevice.deviceId}`;
      } else if (range === "Custom" && customStart && customEnd) {
        url = `https://api.livestockify.com/api/chennai/iot/month?deviceId=${selectedDevice.deviceId}&start=${customStart}&end=${customEnd}`;
      }

      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const formatted = res.data
          .map((item) => {
            const dateObj = new Date(item.timestamp);
            const istDate = new Date(
              dateObj.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
            );

            return {
              day: istDate.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
              fullDate: istDate.toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              }),
              fullTime: istDate.getTime(),
              value: Number(item.sensors?.[selectedMetric]) || 0,
            };
          })
          .sort((a, b) => a.fullTime - b.fullTime);

        setChartData(formatted);
      } catch (error) {
        console.error("Error fetching graph data:", error);
      }
    };

    if (rangeType === "Custom") {
      if (startDate && endDate) fetchGraphByRange("Custom", startDate, endDate);
    } else {
      fetchGraphByRange(rangeType);
    }
  }, [selectedMetric, selectedDevice, rangeType, startDate, endDate]);

  // ------------------------- Download PDF -------------------------
  const handleDownloadPDF = () =>
    downloadIotDeviceReportPdf({
      token,
      device: selectedDevice,
      rangeType,
      startDate,
      endDate,
    });

  // ------------------------- Crusher PDF -------------------------

  const handleCrusherPDF = () =>
    downloadCrusherReportPdf({
      device: selectedDevice,
      selectedRange: rangeType,
      customStart: startDate,
      customEnd: endDate,
      totalCrush: crusherSummary?.totalCrush ?? crusherTodayTotals?.totalCrush ?? 0,
      totalOnTimeMinutes:
        crusherSummary?.totalOnTimeMinutes ??
        crusherTodayTotals?.totalOnTimeMinutes ??
        0,
    });

  // ------------------------- Handle Range Change -------------------------
  const handleRangeChange = (range) => {
    if (range === "Today") {
      setRangeType("Daily");
      return;
    }
    setRangeType(range);
  };

  const isCrusherDevice = isCrusherDeviceCheck(selectedDevice);

  const fetchCrusherSummary = async (
    type = "today",
    customStart = null,
    customEnd = null,
  ) => {
    if (!selectedDevice) return;

    try {
      let url = `https://api.livestockify.com/api/crusher-devices/summary?deviceId=${selectedDevice.deviceId}&type=${type}`;

      if (type === "custom" && customStart && customEnd) {
        url += `&startDate=${customStart}&endDate=${customEnd}`;
      }

      const res = await axios.get(url);

      setCrusherSummary(res.data);
      const formatted = (res.data.graphData || [])
        .map((item) => {
          const dateObj = new Date(item.timestamp);
          const istDate = new Date(
            dateObj.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
          );

          return {
            day: istDate.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            fullDate: istDate.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            fullTime: istDate.getTime(),
            value: Number(item.crush) || 0,
          };
        })
        .sort((a, b) => a.fullTime - b.fullTime);
      setChartData(formatted);
    } catch (error) {
      console.error("Crusher summary error:", error);
    }
  };

  const fetchCrusherTodayTotals = async (deviceId) => {
    try {
      const res = await axios.get(
        `https://api.livestockify.com/api/crusher-devices/totals?deviceId=${deviceId}&type=today`,
      );

      setCrusherTodayTotals(res.data);
    } catch (error) {
      console.error("Crusher today totals error:", error);
    }
  };

  return (
    <div className="min-h-screen w-full min-w-0 text-slate-900">
      <div className="flex w-full flex-col gap-4 sm:gap-5 lg:gap-6">
      {/* Project Header */}
      {projects && (
        <div>
          <h2 className="text-2xl text-left sm:text-3xl font-bold text-[#22592A]">
            {projects.projectName}
          </h2>
          <p className="text-black text-xl text-left">{projects.projectLocation}</p>
        </div>
      )}

      {/* Device Cards Section */}
      <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
        {projects?.devices?.map((device) => {
          const live = deviceDataMap[device.deviceId] || {};
          const sensorEntries = Object.entries(live).filter(
            ([key]) => key.toLowerCase() !== "timestamp",
          );
          return (
            <div
              key={device.deviceUuid}
              onClick={() => handleDeviceClick(device)}
              className={`group flex w-full max-w-[248px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-[#2ec155] via-[#34C759] to-emerald-700 shadow-lg shadow-emerald-900/20 ring-1 ring-white/25 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/25 sm:w-[248px] ${
                selectedDevice?.deviceUuid === device.deviceUuid
                  ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2 px-4 py-3.5 text-white sm:px-5 sm:py-4">
                <div className="min-w-0 flex flex-col items-start text-left">
                  <h3 className="text-base font-semibold leading-snug tracking-tight sm:text-lg">
                    {device.name || "Device"}
                  </h3>
                  <p className="mt-0.5 text-sm text-white/85">
                    Device ID: {device.deviceId}
                  </p>
                </div>
              </div>

              <div className="mt-auto px-3 pb-3 sm:px-4 sm:pb-4">
                <div className="flex h-40 flex-col rounded-xl border border-white/40 bg-white/95 p-3 text-slate-800 shadow-inner backdrop-blur-sm sm:h-44 sm:p-4">
                  <div className="mb-2 border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:text-sm sm:normal-case sm:tracking-normal">
                    {device.name}
                  </div>

                  <div className="flex flex-1 flex-col space-y-1 overflow-y-auto pr-1">
                    {isCrusherDeviceCheck(device) ? (
                      <>
                        <div className="flex items-center justify-between rounded-lg py-1.5 text-sm">
                          <span className="font-medium text-slate-600">
                            Total Crush
                          </span>
                          <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                            {crusherTodayTotals?.totalCrush?.toFixed(2) || "0.00"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg py-1.5 text-sm">
                          <span className="font-medium text-slate-600">
                            Total On Time (Min)
                          </span>
                          <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                            {crusherTodayTotals?.totalOnTimeMinutes || 0}
                          </span>
                        </div>
                      </>
                    ) : sensorEntries.length > 0 ? (
                      sensorEntries.map(([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between rounded-lg py-1.5 text-sm capitalize"
                        >
                          <span className="font-medium text-slate-600">{key}</span>
                          <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                            {typeof value === "number" ? value.toFixed(3) : value}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="flex flex-1 items-center justify-center text-center text-sm text-slate-400">
                        Loading...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Value Boxes */}
      {showValueBoxes && selectedDevice && (
        <>
          {/* {String(selectedDevice.deviceId) === "2024" ? ( */}
          {isCrusherDevice ? (
            <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
              {/* Total Crush */}
              <div
                onClick={() => setShowGraph(true)}
                className="w-full max-w-[248px] shrink-0 rounded-2xl border border-gray-200 bg-white p-5 shadow-md transition hover:border-[#0a5c33] hover:shadow-lg sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    Total Crush (Today)
                  </p>
                </div>

                <p className="text-[#4498D8] mt-3 text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTodayTotals?.totalCrush?.toFixed(2) || "0.00"}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    kg
                  </span>
                </p>
              </div>

              {/* On Time */}
              <div
                onClick={() => setShowGraph(true)}
                className="w-full max-w-[248px] shrink-0 rounded-2xl border border-gray-200 bg-white p-5 shadow-md transition hover:border-[#0a5c33] hover:shadow-lg sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    On Time (Minutes)
                  </p>
                </div>

                <p className="text-[#4498D8] mt-3 text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTodayTotals?.totalOnTimeMinutes || 0}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    min
                  </span>
                </p>
              </div>
            </div>
          ) : (
            selectedDevice &&
            deviceDataMap[selectedDevice?.deviceId] &&
            Object.keys(deviceDataMap[selectedDevice?.deviceId] || {}).length >
              0 && (
              <div className="w-full min-w-0">
                <h3 className="mb-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-800 sm:text-sm">
                  Sensors
                </h3>
                <div className="-mx-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden pb-2 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:snap-none sm:overflow-visible sm:pb-0">
                  <div className="flex flex-nowrap gap-3 px-1 sm:flex-wrap sm:justify-start sm:gap-5 sm:px-0">
                {Object.entries(deviceDataMap[selectedDevice?.deviceId] || {})
                  .filter(([key]) => key.toLowerCase() !== "timestamp")
                  .map(([key, value]) => (
                    <div
                      key={key}
                      onClick={() => handleMetricClick(key)}
                      className="min-w-[156px] w-[156px] shrink-0 snap-start rounded-xl border border-gray-200 bg-white p-3 shadow-md transition hover:border-[#0a5c33] hover:shadow-lg cursor-pointer sm:min-w-[248px] sm:w-[248px] sm:rounded-2xl sm:p-5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold capitalize leading-snug text-gray-700 sm:text-lg">
                          {key}
                        </p>
                      </div>

                      <p className="mt-1.5 text-left text-2xl font-bold leading-tight text-[#4498D8] sm:mt-3 sm:text-4xl sm:leading-tight lg:text-5xl">
                        {typeof value === "number" ? value.toFixed(2) : value}
                        <span className="ml-0.5 text-sm font-semibold text-gray-500 sm:ml-1.5 sm:text-lg lg:text-xl">
                          {key.toLowerCase() === "ph"
                            ? "pH"
                            : key.toLowerCase() === "temperature"
                              ? "°C"
                              : ""}
                        </span>
                      </p>
                    </div>
                  ))}
                  </div>
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* Graph Section */}
      {showGraph && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6">
          <div className="mb-4 flex w-full min-w-0 items-center justify-between gap-2">
            <h3 className="min-w-0 flex-1 truncate text-left text-lg font-semibold capitalize text-slate-900">
              {isCrusherDevice ? "Crusher Summary" : selectedMetric}
            </h3>
            <select
              className="ml-2 shrink-0 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25"
              value={rangeType}
              onChange={(e) => handleRangeChange(e.target.value)}
            >
              <option>Daily</option>
              <option>Weekly</option>
              <option>Monthly</option>
              <option>Custom</option>
            </select>
          </div>

          {isCrusherDevice && crusherSummary && (
            <div className="mb-4 flex flex-wrap items-center gap-x-8 gap-y-2">
              <div>
                <p className="text-sm text-gray-500">Total Crushed</p>
                <p className="text-lg font-semibold text-[#34C759]">
                  {crusherSummary.totalCrush?.toFixed(2)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Total On Time (Minutes)</p>
                <p className="text-lg font-semibold text-[#34C759]">
                  {crusherSummary.totalOnTimeMinutes}
                </p>
              </div>
            </div>
          )}

          {rangeType === "Custom" && (
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          )}

          <div
            ref={chartRef}
            className={`w-full min-w-0 overflow-y-hidden rounded-xl border border-slate-100 bg-slate-50/40 pb-1 [-webkit-tap-highlight-color:transparent] outline-none focus:outline-none focus-visible:outline-none ${
              chartHScrollEnabled ? "overflow-x-auto" : "overflow-x-hidden"
            }`}
          >
            <div
              className="h-[260px] min-h-[240px] sm:h-[300px] outline-none [-webkit-tap-highlight-color:transparent] [&_.recharts-cartesian-axis-tick_text]:outline-none [&_.recharts-layer]:outline-none [&_.recharts-responsive-container]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-surface]:ring-0 [&_.recharts-surface:focus]:outline-none [&_.recharts-surface:focus-visible]:outline-none [&_.recharts-tooltip-cursor]:hidden [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper:focus-visible]:outline-none [&_g]:outline-none [&_path]:outline-none [&_rect]:outline-none [&_svg]:outline-none [&_svg]:ring-0 [&_svg:focus]:outline-none [&_svg:focus-visible]:outline-none"
              style={
                chartHScrollEnabled && chartScrollWidthPx
                  ? {
                      width: `${chartScrollWidthPx}px`,
                      minWidth: `${chartScrollWidthPx}px`,
                    }
                  : { width: "100%", minWidth: 0 }
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  accessibilityLayer={false}
                  tabIndex={-1}
                  focusable={false}
                  style={{ outline: "none" }}
                  margin={{
                    top: 8,
                    right: 10,
                    left: 0,
                    bottom: 8,
                  }}
                >
                <defs>
                  <linearGradient id="userMetricFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e2e8f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="fullTime"
                  type="number"
                  scale="time"
                  domain={["dataMin", "dataMax"]}
                  tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={formatChartAxisTick}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                />
                <YAxis
                  width={34}
                  tickCount={5}
                  tick={{ fontSize: 10, fill: "#64748b", dx: -2 }}
                  tickLine={false}
                  axisLine={false}
                  domain={["auto", "auto"]}
                  tickFormatter={(v) =>
                    typeof v === "number" && Number.isFinite(v)
                      ? String(Number(v.toFixed(2)))
                      : String(v ?? "")
                  }
                />
                <Tooltip
                  cursor={false}
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ outline: "none", boxShadow: "none", zIndex: 20 }}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                    outline: "none",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.07)",
                  }}
                  labelFormatter={(_label, payload) => {
                    if (payload?.length > 0) {
                      return payload[0].payload.fullDate;
                    }
                    return _label;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  name={String(selectedMetric ?? "value")}
                  stroke="#0284c7"
                  strokeWidth={2}
                  fill="url(#userMetricFill)"
                  dot={{ r: 2, fill: "#0369a1" }}
                  activeDot={{ r: 5, stroke: "none", fill: "#0369a1" }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowDownloadPopup(true)}
                className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition"
              >
                Download Report
              </button>
            </div>
          }
        </div>
      )}

      {/* Download Popup */}
      {showDownloadPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-2xl shadow-slate-900/15 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Download Report
                </h3>
                <p className="mt-1 truncate text-sm text-slate-500">
                  {selectedDevice?.name || "Device"}
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {selectedMetric || "Sensor"}
              </span>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Range
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {["Today", "Weekly", "Monthly", "Custom"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      (r === "Today" ? rangeType === "Daily" : rangeType === r)
                        ? "border-blue-500 bg-blue-500 text-white shadow-sm shadow-blue-500/25"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                    onClick={() => handleRangeChange(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {rangeType === "Custom" && (
              <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Custom Date Range
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2.5">
              <button
                type="button"
                className="flex-1 rounded-lg bg-blue-500 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
                onClick={() => {
                  setShowDownloadPopup(false);

                  if (isCrusherDevice) {
                    handleCrusherPDF();
                  } else {
                    handleDownloadPDF();
                  }
                }}
              >
                Download Report
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                onClick={() => setShowDownloadPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UserDashboard;
